import {
  useCheckInMutation,
  useCheckInQuery,
  useCheckOutMutation,
} from "@gymflow/api";
import { useQuery } from "@tanstack/react-query";
import { apiV2 } from "libs/api/src/lib/apiV2";
import capitalize from "lodash/capitalize";
import { useCallback, useContext } from "react";
import { useHistory } from "react-router-dom";

import { ConfirmModal } from "../components/templates";
import { ModalContext } from "../providers";
import useGymflowModels from "../store";
import { usePortalRoutes } from "./usePortalRoutes";

export function useAccessToggleButton({
  userMemberId,
  onCheckInOutFinished,
}: {
  userMemberId?: string;
  onCheckInOutFinished?: (isCheckedIn: boolean) => void;
}) {
  const { api } = useGymflowModels();
  const { data: checkInContainer } = useCheckInQuery({ api, userMemberId });
  const { setModal, hide } = useContext(ModalContext);
  const history = useHistory();
  const { createMemberLink } = usePortalRoutes();

  const {
    mutateAsync: checkIn,
    isSuccess: didCheckIn,
    isPending: isPendingCheckIn,
  } = useCheckInMutation(
    { api },
    {
      onSuccess: () => onCheckInOutFinished?.(true),
    },
  );
  const {
    mutateAsync: checkOut,
    isSuccess: didCheckOut,
    isPending: isPendingCheckOut,
  } = useCheckOutMutation(
    { api },
    {
      onSuccess: () => onCheckInOutFinished?.(false),
    },
  );
  const checkedInOrOut = didCheckIn || didCheckOut;

  const { data: userMember } = useQuery(
    apiV2.staff.member.queries.idDeprecated(userMemberId),
  );

  const userActiveSubscription = userMember?.subscriptions?.find(
    (e) => e.active && e.membershipBean,
  );

  const toggle = useCallback(async () => {
    if (isPendingCheckIn || isPendingCheckOut || !userMember) return;
    if (!checkInContainer?.isCheckedIn) {
      if (!userActiveSubscription) {
        setModal(
          <ConfirmModal
            title="No Membership"
            cancelText="View"
            confirmText="Yes"
            onCancel={() => {
              hide();
              history.push(createMemberLink(userMember.id));
            }}
            onConfirm={async () => {
              await checkIn({
                userMemberId: userMember.id,
                payload: { guest: true },
              });
              hide();
            }}
          >
            {userMember.firstName} {userMember.lastName} doesn’t have a
            membership, do you want to check-in as a guest ?
          </ConfirmModal>,
        );
        return;
      }

      if (userActiveSubscription.status !== "ACTIVE") {
        setModal(
          <ConfirmModal
            title="No Membership"
            cancelText="View"
            confirmText="Yes"
            onCancel={() => {
              hide();
              history.push(createMemberLink(userMember.id));
            }}
            onConfirm={async () => {
              await checkIn({
                userMemberId: userMember.id,
                payload: { guest: true },
              });
              hide();
            }}
            type="danger"
          >
            {userMember.firstName} {userMember.lastName} subscription is{" "}
            <b>{capitalize(userActiveSubscription.status)} </b> do you still
            want to check in?
          </ConfirmModal>,
        );
        return;
      }
    }
    if (checkInContainer?.isCheckedIn) {
      await checkOut({ userMemberId: userMember.id });
    } else {
      await checkIn({
        userMemberId: userMember.id,
        payload: { guest: false },
      });
    }
  }, [
    checkIn,
    checkInContainer?.isCheckedIn,
    checkOut,
    createMemberLink,
    hide,
    history,
    isPendingCheckIn,
    isPendingCheckOut,
    setModal,
    userActiveSubscription,
    userMember,
  ]);

  return {
    toggle,
    isReady: !!userMember && !!checkInContainer,
    checkedInOrOut,
    isCheckedIn: checkInContainer?.isCheckedIn,
    didCheckIn,
    didCheckOut,
  };
}
